import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface PdfData {
  logType: string;
  serialNumber: string;
  companyName?: string;
  siteName?: string;
  chartImageUrl?: string;
  chartElement?: HTMLElement | null;
  tableData?: any[];
  tableColumns?: Array<{ header: string; accessorKey: string }>;
}

export async function generatePdf({
  logType,
  serialNumber,
  companyName,
  siteName,
  chartImageUrl,
  chartElement,
  tableData = [],
  tableColumns = [],
}: PdfData): Promise<void> {
  // Configuration Log는 컬럼이 많아서 가로 방향 사용
  const orientation = logType === 'Configuration' ? 'l' : 'p';
  const pdf = new jsPDF(orientation, 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  let yPosition = margin;

  // 헤더 정보
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Data Log Report', margin, yPosition);
  yPosition += 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Log Type: ${logType}`, margin, yPosition);
  yPosition += 5;
  pdf.text(`Serial Number: ${serialNumber}`, margin, yPosition);
  yPosition += 5;
  if (companyName) {
    pdf.text(`Company: ${companyName}`, margin, yPosition);
    yPosition += 5;
  }
  if (siteName) {
    pdf.text(`Site: ${siteName}`, margin, yPosition);
    yPosition += 5;
  }
  yPosition += 5;

  // 차트 이미지 추가
  let chartImgData: string | null = null;
  let chartImgWidth = 0;
  let chartImgHeight = 0;

  if (chartImageUrl) {
    // ECharts에서 직접 추출한 이미지 URL 사용
    chartImgData = chartImageUrl;
    // 이미지 크기 추정 (A4 너비에 맞춤)
    chartImgWidth = contentWidth;
    chartImgHeight = 60; // 기본 높이
  } else if (chartElement) {
    try {
      // html2canvas로 차트 캡처
      const chartCanvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      chartImgData = chartCanvas.toDataURL('image/png');
      chartImgWidth = contentWidth;
      chartImgHeight = (chartCanvas.height * chartImgWidth) / chartCanvas.width;
    } catch (error) {
      console.error('Failed to capture chart:', error);
    }
  }

  if (chartImgData) {
    // 페이지 높이 확인
    if (yPosition + chartImgHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    pdf.addImage(chartImgData, 'PNG', margin, yPosition, chartImgWidth, chartImgHeight);
    yPosition += chartImgHeight + 5;
  }

  // 테이블 데이터 추가
  if (tableData.length > 0 && tableColumns.length > 0) {
    // 페이지 확인 및 새 페이지 추가
    if (yPosition > pageHeight - margin - 30) {
      pdf.addPage();
      yPosition = margin;
    }

    // 테이블 헤더
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Data Table', margin, yPosition);
    yPosition += 5;

    // 컬럼 너비 계산 (더 넓게)
    const colCount = tableColumns.length;
    const colWidth = contentWidth / colCount;

    // 행 높이
    const rowHeight = 6;
    const headerRowHeight = 7;

    // 테이블 헤더 행
    const headerStartY = yPosition - headerRowHeight + 2;
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    let xPosition = margin;

    // 헤더 배경색 (선 없이 채우기만)
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, headerStartY, contentWidth, headerRowHeight, 'F');

    // 헤더 외곽선 그리기
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);
    // 상단 선
    pdf.line(margin, headerStartY, margin + contentWidth, headerStartY);
    // 좌측 선
    pdf.line(margin, headerStartY, margin, headerStartY + headerRowHeight);
    // 우측 선
    pdf.line(
      margin + contentWidth,
      headerStartY,
      margin + contentWidth,
      headerStartY + headerRowHeight
    );
    // 하단 선 (더 굵게)
    pdf.setLineWidth(0.3);
    pdf.setDrawColor(100, 100, 100);
    pdf.line(
      margin,
      headerStartY + headerRowHeight,
      margin + contentWidth,
      headerStartY + headerRowHeight
    );

    // 헤더 텍스트 및 세로선
    pdf.setLineWidth(0.1);
    pdf.setDrawColor(200, 200, 200);
    tableColumns.forEach((col, index) => {
      const header = col.header
        .replace('시간', 'Time')
        .replace('Gas', 'Gas')
        .replace('Temp', 'Temp')
        .replace('Status', 'Status');

      // 세로선 그리기 (마지막 컬럼 제외, 우측 선과 겹치지 않도록)
      if (index < tableColumns.length - 1) {
        const verticalLineX = xPosition + colWidth;
        // 우측 경계와 겹치지 않는지 확인
        if (verticalLineX < margin + contentWidth - 0.1) {
          pdf.line(verticalLineX, headerStartY, verticalLineX, headerStartY + headerRowHeight);
        }
      }

      // 텍스트 (패딩 고려)
      pdf.setTextColor(0, 0, 0);
      pdf.text(header, xPosition + 2, yPosition, {
        maxWidth: colWidth - 4,
        align: 'left',
      });

      xPosition += colWidth;
    });

    yPosition = headerStartY + headerRowHeight;
    yPosition += 1; // 여백 1mm
    let isFirstRowOnPage = true; // 이 페이지 첫 데이터행 표시

    // 테이블 데이터 행
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6);
    pdf.setTextColor(0, 0, 0);

    tableData.forEach(row => {
      // 페이지 확인
      if (yPosition + rowHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;

        // 새 페이지에서 헤더 다시 그리기
        const newHeaderStartY = yPosition - headerRowHeight + 2;
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, newHeaderStartY, contentWidth, headerRowHeight, 'F');

        // 헤더 외곽선 그리기
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.1);
        // 상단 선
        pdf.line(margin, newHeaderStartY, margin + contentWidth, newHeaderStartY);
        // 좌측 선
        pdf.line(margin, newHeaderStartY, margin, newHeaderStartY + headerRowHeight);
        // 우측 선
        pdf.line(
          margin + contentWidth,
          newHeaderStartY,
          margin + contentWidth,
          newHeaderStartY + headerRowHeight
        );
        // 하단 선 (더 굵게)
        pdf.setLineWidth(0.3);
        pdf.setDrawColor(100, 100, 100);
        pdf.line(
          margin,
          newHeaderStartY + headerRowHeight,
          margin + contentWidth,
          newHeaderStartY + headerRowHeight
        );

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        xPosition = margin;
        pdf.setLineWidth(0.1);
        pdf.setDrawColor(200, 200, 200);
        tableColumns.forEach((col, index) => {
          const header = col.header
            .replace('시간', 'Time')
            .replace('Gas', 'Gas')
            .replace('Temp', 'Temp')
            .replace('Status', 'Status');

          // 세로선 그리기 (마지막 컬럼 제외, 우측 선과 겹치지 않도록)
          if (index < tableColumns.length - 1) {
            const verticalLineX = xPosition + colWidth;
            // 우측 경계와 겹치지 않는지 확인
            if (verticalLineX < margin + contentWidth - 0.1) {
              pdf.line(
                verticalLineX,
                newHeaderStartY,
                verticalLineX,
                newHeaderStartY + headerRowHeight
              );
            }
          }

          pdf.text(header, xPosition + 2, yPosition, {
            maxWidth: colWidth - 4,
            align: 'left',
          });

          xPosition += colWidth;
        });

        yPosition = newHeaderStartY + headerRowHeight;
        yPosition += 1; // 여백 1mm
        isFirstRowOnPage = true; // 새 페이지 첫 데이터행 표시
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
      }

      const rowTopY = yPosition;
      const rowBottomY = yPosition + rowHeight;

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.1);

      // 페이지 첫 데이터행이면 상단선 생략 (헤더 하단선과 중복 방지)
      if (!isFirstRowOnPage) {
        pdf.line(margin, rowTopY, margin + contentWidth, rowTopY);
      }

      // 좌측/우측 선
      pdf.line(margin, rowTopY, margin, rowBottomY);
      pdf.line(margin + contentWidth, rowTopY, margin + contentWidth, rowBottomY);

      // 데이터 셀
      xPosition = margin;
      tableColumns.forEach((col, colIndex) => {
        let value = row[col.accessorKey] ?? '';
        if (typeof value === 'string') {
          value = value
            .replace('Gas Calibrating', 'Calibrating')
            .replace('Low Alarm', 'Low Alarm')
            .replace('High Alarm', 'High Alarm')
            .replace('Normal', 'Normal');
        }

        // 세로선
        if (colIndex < tableColumns.length - 1) {
          pdf.line(xPosition + colWidth, rowTopY, xPosition + colWidth, rowBottomY);
        }

        // 텍스트 baseline 보정
        pdf.text(String(value), xPosition + 2, rowTopY + 4, {
          maxWidth: colWidth - 4,
          align: 'left',
        });

        xPosition += colWidth;
      });

      // 하단선
      pdf.line(margin, rowBottomY, margin + contentWidth, rowBottomY);

      // 첫 행 처리 끝
      isFirstRowOnPage = false;

      yPosition += rowHeight;
    });
  }

  // PDF 다운로드
  const fileName = `${logType}_${serialNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
  try {
    pdf.save(fileName);
  } catch (error) {
    console.error('Error calling pdf.save:', error);
    throw error;
  }
}
